import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pencil } from 'lucide-react';
import { type FC, type KeyboardEvent, useState } from 'react';

const HEADLINE_LABEL_CLASS = 'mr-1.5 text-xs uppercase tracking-wide text-muted-foreground';

/** What a homeowner reads in a recall list. A blank save clears it, handing it back to the classifier. */
export const RecallHeadline: FC<{
    headline: string | undefined;
    adminEdited: boolean;
    disabled: boolean;
    onSave: (headline: string) => Promise<boolean>;
}> = ({ headline, adminEdited, disabled, onSave }) => {
    const [draft, setDraft] = useState<string>();

    const save = async () => {
        if (draft === undefined || disabled) return;
        if (draft.trim() === (headline ?? '') || (await onSave(draft))) setDraft(undefined);
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') void save();
        if (event.key === 'Escape') setDraft(undefined);
    };

    if (draft !== undefined) {
        return (
            <div className="flex items-center gap-2">
                <span className={HEADLINE_LABEL_CLASS}>Headline</span>
                <Input
                    autoFocus
                    aria-label="Headline"
                    className="h-8 flex-1"
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={onKeyDown}
                />
                <Button size="sm" disabled={disabled} onClick={() => void save()}>
                    Save
                </Button>
                <Button size="sm" variant="outline" disabled={disabled} onClick={() => setDraft(undefined)}>
                    Cancel
                </Button>
            </div>
        );
    }

    return (
        <p className="flex items-center text-sm">
            <span className={HEADLINE_LABEL_CLASS}>Headline</span>
            {headline ?? <span className="text-muted-foreground">None</span>}
            <Button
                size="icon-xs"
                variant="ghost"
                className="ml-1"
                aria-label="Edit headline"
                disabled={disabled}
                onClick={() => setDraft(headline ?? '')}
            >
                <Pencil />
            </Button>
            {adminEdited && (
                <Badge variant="outline" className="ml-1">
                    admin edited
                </Badge>
            )}
        </p>
    );
};
